/** Engine/app manifest inspection for the dev loop: permissions extraction + structural validation. */
import { readFile } from 'node:fs/promises'
import { parse } from 'yaml'

export interface ManifestDocument {
  'api-version'?: string
  kind?: string
  metadata?: {
    name?: string
    'display-name'?: string
    version?: string
    description?: string
    [key: string]: unknown
  }
  spec?: {
    runtimes?: Record<string, unknown>
    'file-types'?: { pattern?: string; extensions?: string[]; priority?: number }[]
    'execution-modes'?: string[]
    tools?: { name?: string; description?: string; [key: string]: unknown }[]
    capabilities?: string[]
    permissions?: string[]
    resources?: Record<string, unknown>
    sandbox?: Record<string, unknown>
    entry?: unknown
    'entry-points'?: unknown[]
    install?: { from?: string; to?: string; mode?: string; mime?: string }[]
    'install-root'?: string
    scopes?: string[]
    [key: string]: unknown
  }
  packaging?: { publisher?: string; files?: string[]; [key: string]: unknown }
  [key: string]: unknown
}

/** Lenient manifest load (no required fields) — validation reports problems instead of throwing. */
export async function readManifestDocument(path: string): Promise<ManifestDocument> {
  const text = await readFile(path, 'utf8')
  const doc = parse(text) as ManifestDocument
  if (!doc || typeof doc !== 'object') throw new Error(`${path} is not a YAML manifest.`)
  return doc
}

/** The permission scopes a manifest declares under `spec.permissions`. */
export function manifestPermissions(manifest: ManifestDocument): string[] {
  const permissions = manifest.spec?.permissions
  if (!Array.isArray(permissions)) return []
  return permissions.filter((p): p is string => typeof p === 'string')
}

export interface ValidationIssue {
  level: 'error' | 'warning'
  message: string
}

/** Structural validation mirroring the platform's registration-time checks. */
export function validateManifest(manifest: ManifestDocument): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const error = (message: string) => issues.push({ level: 'error', message })
  const warning = (message: string) => issues.push({ level: 'warning', message })

  const kind = manifest.kind
  if (!kind) {
    error("missing 'kind' (ExecutionEngine | Application | Bundle)")
    return issues
  }
  if (!['ExecutionEngine', 'Application', 'Bundle'].includes(kind)) {
    error(`unknown kind '${kind}' (expected ExecutionEngine, Application or Bundle)`)
  }
  if (!manifest['api-version']) warning("missing 'api-version' (expected verentis.io/v1)")

  const meta = manifest.metadata ?? {}
  for (const field of ['name', 'display-name', 'version'] as const) {
    if (!meta[field]) error(`metadata.${field} is required`)
  }

  const spec = manifest.spec ?? {}

  if (kind === 'ExecutionEngine') {
    const runtimes = spec.runtimes ?? {}
    if (Object.keys(runtimes).length === 0) {
      error('spec.runtimes must declare at least one runtime (docker and/or wasm)')
    }
    const docker = runtimes['docker'] as { image?: string } | undefined
    if (docker && !docker.image) error('spec.runtimes.docker.image is required')
    const wasm = runtimes['wasm'] as { module?: string } | undefined
    if (wasm && !wasm.module) error('spec.runtimes.wasm.module is required')

    if (!spec['file-types']?.length) warning('spec.file-types is empty — the engine will never be auto-resolved for a file')
    for (const fileType of spec['file-types'] ?? []) {
      if (!fileType.pattern) error("every spec.file-types[] entry needs a 'pattern' (MIME type)")
    }
    for (const tool of spec.tools ?? []) {
      if (!tool.name) error("every spec.tools[] entry needs a 'name'")
    }
    if (!spec['execution-modes']?.length) warning('spec.execution-modes is empty (expected e.g. request-response)')
    if (!manifestPermissions(manifest).length) warning('spec.permissions is empty — the run token will carry no VFS scopes')
  }

  if (kind === 'Application') {
    const hasIframe = !!spec.entry || !!(spec['entry-points'] as unknown[] | undefined)?.length
    const hasInstall = Array.isArray(spec.install) && spec.install.length > 0
    const hasScopes = Array.isArray(spec.scopes) && spec.scopes.length > 0
    if (!hasIframe && !hasInstall && !hasScopes) {
      error(
        'an Application must declare at least one contribution: spec.entry / spec.entry-points[] (iframe UI), ' +
          'spec.install[] (installed files), or spec.scopes[] (identity)',
      )
    }
  }

  validateInstallDirectives(spec.install, issues)

  const packaging = manifest.packaging ?? {}
  if (!packaging.publisher) warning('packaging.publisher is not set — `verentis pack`/`publish` will refuse the package')

  return issues
}

/** Validates a `spec.install` map (present on apps that place files into the workspace VFS). */
function validateInstallDirectives(
  install: { from?: string; to?: string; mode?: string; mime?: string }[] | undefined,
  issues: ValidationIssue[],
): void {
  if (install === undefined) return
  const error = (message: string) => issues.push({ level: 'error', message })

  if (!Array.isArray(install)) {
    error('spec.install must be a list of install directives')
    return
  }

  install.forEach((directive, index) => {
    const at = `spec.install[${index}]`
    if (!directive || typeof directive !== 'object') {
      error(`${at} must be an object with 'from' and 'to'`)
      return
    }

    if (!directive.from || typeof directive.from !== 'string') {
      error(`${at}.from is required (a path/glob relative to the package payload)`)
    } else if (directive.from.startsWith('/') || directive.from.split('/').includes('..')) {
      error(`${at}.from must be a relative path inside the package payload (no leading '/' or '..')`)
    }

    if (!directive.to || typeof directive.to !== 'string') {
      error(`${at}.to is required (a workspace-absolute destination path)`)
    } else {
      if (!directive.to.startsWith('/')) error(`${at}.to must be a workspace-absolute path (start with '/')`)
      if (directive.to.split('/').includes('..')) error(`${at}.to must not contain '..'`)
    }

    if (directive.mode !== undefined && directive.mode !== 'seed' && directive.mode !== 'managed') {
      error(`${at}.mode must be 'seed' or 'managed' (got '${directive.mode}')`)
    }
  })
}
